import * as BlazeSDK from '@wscsports/blaze-web-sdk';
import type { IBlazeSDKOptions } from '@wscsports/blaze-web-sdk';

/**
 * Blaze SDK Manager - Singleton pattern for managing Blaze SDK initialization
 */
class BlazeSDKManager {
  private _initializedKeys: Set<string> = new Set();
  private _pendingInitializations: Map<string, Promise<void>> = new Map();
  private _sdkReady: boolean = false;
  private _eventListeners: Map<string, Array<(...args: any[]) => void>> = new Map();
  private _connectionListeners: Array<() => void> = [];

  /**
   * Initialize Blaze SDK for a specific API key
   */
  async initialize(apiKey: string, options: Partial<IBlazeSDKOptions & { layoutDirection?: string }> = {}): Promise<void> {
    if (!apiKey) {
      throw new Error('API key is required');
    }

    // Prevent duplicate initialization
    if (this._initializedKeys?.has(apiKey)) {
      return Promise.resolve();
    }

    if (this._pendingInitializations.has(apiKey)) {
      return this._pendingInitializations?.get(apiKey)!;
    }

    const initPromise = this._performInitialization(apiKey, options);
    this._pendingInitializations.set(apiKey, initPromise);

    try {
      await initPromise;
      this._initializedKeys.add(apiKey);
      this._pendingInitializations.delete(apiKey);
    } catch (error) {
      this._pendingInitializations.delete(apiKey);
      throw error;
    }
  }

  /**
   * Perform actual SDK initialization
   */
  private async _performInitialization(apiKey: string, options: Partial<IBlazeSDKOptions> = {}): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('SDK initialization timeout'));
      }, 10000); 

      const onConnect = () => {
        clearTimeout(timeout);
        this._sdkReady = true;
        document.removeEventListener('onBlazeSDKConnect', onConnect);
        resolve();
      };

      this._connectionListeners.push(onConnect);
      document.addEventListener('onBlazeSDKConnect', onConnect);

      // Initialize Blaze SDK
      if (!BlazeSDK.isInitialized()) {
        BlazeSDK.Initialize(apiKey, {
          geoLocation: options.geoLocation || '',
          doNotTrack: options.doNotTrack,
          externalUserId: options.externalUserId,
          layoutDirection: options.layoutDirection,
          ...options
        });
      } else {
        clearTimeout(timeout);
        resolve();
      }
    });
  }

  isReady(): boolean {
    return this._sdkReady;
  }

  getSDK(): typeof BlazeSDK {
    if (!this.isReady()) {
      throw new Error('Blaze SDK not initialized');
    }
    return BlazeSDK;
  }


  cleanup(): void {
    this._connectionListeners.forEach(listener => {
      document.removeEventListener('onBlazeSDKConnect', listener);
    });
    this._connectionListeners = [];
    
    this._eventListeners.clear();
    
    this._sdkReady = false;
    this._initializedKeys.clear();
    this._pendingInitializations.clear();
  }
}

const blazeSDKManager = new BlazeSDKManager();
export default blazeSDKManager; 